import { Component, DebugElement, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as ScanditSDK from "scandit-sdk";
import { BarcodePicker, Logger, ScanResult } from "scandit-sdk";
import { combineLatest, fromEvent, map, Observable, Subscription, tap } from "rxjs";
import { ConnectivityStateService } from 'src/app/shared/services/connectivity-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProductService } from 'src/app/products/services/product.service';
import { IProduct } from 'src/app/products/models/product';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BasketService } from 'src/app/basket/services/basket.service';
import { ICreateBasketItemRequest, ICreateBasketRequest } from 'src/app/basket/models/basket';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { OfflineBasketStorageService} from 'src/app/basket/storage/offline-basket-store.service'
import { environment } from 'src/environments/environment';
import { translate } from '@jsverse/transloco';
import { ICompany } from 'src/app/company/models/company';
import { select, Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';

@UntilDestroy()
@Component({
    selector: 'app-scanner',
    templateUrl: './scanner.component.html',
    styleUrls: ['./scanner.component.scss'],
    standalone: false
})
export class ScannerComponent implements OnInit {
  @Input() isAdminView : boolean = false;
  @Input() currentCompany: ICompany = null;

  vm$: Observable<any>;

  constructor(
    private connectivityStateService : ConnectivityStateService,
    private productService: ProductService,
    private basketService : BasketService,
    private store: Store<AppStateInterface>,
    private appNotificationService: AppNotificationService,
    private offlineBasketStore: OfflineBasketStorageService) {
      this.vm$ = combineLatest([
        this.store.pipe(select(currentEmployeeSelector))
      ]).pipe(
        map(([
          currentEmployee
        ]) => ({
          currentEmployee
        }))
      )
    }

  @ViewChild("barcodePicker") 
  barcodePickerElement!: ElementRef<HTMLDivElement & { barcodePicker: BarcodePicker; }>;
  scanditScannerReady = false;
  showScanner = true;
  isOnline = false;
  currentProduct : IProduct;
  scanditLicense = environment.scanditLicense;
  isProduction = environment.production;

  addToCartForm: UntypedFormGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    name: new UntypedFormControl(null),
    internalNumber: new UntypedFormControl(null, Validators.required),
    quantity: new UntypedFormControl(1, Validators.required),
  });

  ngOnInit(): void {
    this.initConnectionState();
  }

  ngAfterViewInit() {
    this.initScanditLicense();
  }
    

  public connectionState$ : Observable<boolean> =
  this.connectivityStateService.connectionState$;

  ////////////////////////////////////////////////////////////////
  // WARNING
  // Do not disable Scanner by remive it via ngIf
  // this.barcodePickerElement will be undefined after first scan
  // scanner wont work after first scan 
  ////////////////////////////////////////////////////////////////

  initScanditLicense(){
    this.barcodePickerElement.nativeElement.setAttribute("configure.licenseKey", this.scanditLicense);
    this.barcodePickerElement.nativeElement.setAttribute("configure", "true");
  }

  initConnectionState(){
    this.connectionState$.pipe(untilDestroyed(this))
      .subscribe(status => {
        this.isOnline = status
      })
  }

  showAddToCartSuccess(){
    this.appNotificationService.notification$.next({message:translate('basket.createSuccess'), style: "success"});
  }

  //Scandit Scanner Start

  scanSuccessHandler(event: string){
    this.disabledScanner();
    
    if(this.isOnline){
      this.productService.getProductByInternalNumber(event)
        .pipe(untilDestroyed(this))
        .subscribe(product => {
          if(product){
            this.currentProduct = product
            this.addToCartForm.patchValue({
              id: product.id,
              name: product.name,
              internalNumber: product.internalNumber,
              quantity: 1
            })
          }

        },error => {
          this.enableScanner()
        });

    } else {
      this.currentProduct = {
        id: null,
        name: null,
        internalNumber: event,
        isActive: true,
        createdBy: null,
        createdOn: null
      }
      this.addToCartForm.patchValue({
        internalNumber: event,
        quantity: 1
      })
    }
  }

  public onReady(): void {
    this.scanditScannerReady = true;
    this.barcodePickerElement.nativeElement.barcodePicker.setVisible(true).resumeScanning();
  }

  public onScan(scanResult: { detail: ScanResult }): void {
    const calculatedString = scanResult.detail.barcodes.reduce((result, barcode) => {
      return `${result} ${ScanditSDK.Barcode.Symbology.toHumanizedName(barcode.symbology)} : ${barcode.data}`;
    }, "");

    this.scanSuccessHandler(calculatedString);
  }

  public addScanToList(scanResult: any): void {
    scanResult.detail.barcodes.forEach((barcode: { data: string; }) => {
      this.scanSuccessHandler(barcode.data);
    });
  }

  public onError(event : any){
    this.appNotificationService.notification$.next({message: translate('scanner.error'), style: 'error'})
  }

  startScanner(){
    this.enableScanner()
    this.currentProduct = null;
  }

  enableScanner(){
    this.showScanner = true;
    this.barcodePickerElement.nativeElement.barcodePicker.setVisible(true).resumeScanning();
  }

  disabledScanner(){
    this.showScanner = false;
    this.barcodePickerElement.nativeElement.barcodePicker.setVisible(false).pauseScanning();
  }

  addToCart(currentEmployee: any){
    const formValues = this.addToCartForm.value;

    if(this.isOnline && !this.isAdminView){

    const request : ICreateBasketItemRequest = {
      productId: formValues.id,
      quantity: formValues.quantity,
      companyId: currentEmployee.company.id
    }

    this.basketService.addBasketItem(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
          this.addToCartForm.reset();
          this.showAddToCartSuccess();
          this.enableScanner();
        })
    }
    else if (this.isOnline && this.isAdminView){
      const request: ICreateBasketItemRequest = {
        productId: formValues.id,
        quantity: formValues.quantity,
        companyId: this.currentCompany.id
      }
      
      this.basketService.addBasketItem(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.addToCartForm.reset();
        this.showAddToCartSuccess();
        this.enableScanner();
    });
    }
    else {
      this.offlineBasketStore.addProduct({internalNumber: formValues.internalNumber, quantity: formValues.quantity, companyId: null});
      this.addToCartForm.reset();
      this.enableScanner();
    }
    
  }
}